import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import { z } from 'zod'
import { CorrectionsScreen } from '@/features/hr-attendance-corrections/corrections-screen'
import { HybridWorkRequestsPanel } from '@/features/hr-hybrid-work'
import { WorkEntriesPanel } from '@/features/hr-work-entries'
import { hasGrant } from '@/lib/me'

/**
 * HR's attendance requests: corrections (FR-HR-011), freelance hours and work
 * from home.
 *
 * The grant guard keeps a user without `attendance.view` out of a screen the
 * API refuses anyway; deciding additionally needs `attendance_correction.approve`
 * and direct writes need `attendance.edit`, and both are enforced server-side.
 *
 * ## Why this renders an Outlet
 *
 * `attendance-corrections.$requestId.tsx` is a CHILD of this route in flat
 * file routing, so without an Outlet it never mounts and the detail URL
 * silently rendered this list instead — the same shape as `expenses.tsx`;
 * see `employees.$employeeId.tsx` for the full note and the tidier fix.
 */
const attendanceRequestsSearchSchema = z.object({
  tab: z.enum(['corrections', 'freelance-hours', 'remote-work']).optional(),
  /** Opens an approved manual-hours record linked from payroll. */
  entry_id: z.string().optional(),
})

/*
 * The freelance hour and work-from-home queues are other features' screens, so
 * the route composes them in, the way `leave.tsx` hands in its hourly panel.
 */
export const Route = createFileRoute('/_authenticated/hr/attendance-corrections')({
  validateSearch: attendanceRequestsSearchSchema,
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'attendance.view')) {
      throw redirect({ to: '/' })
    }
  },
  component: AttendanceCorrectionsRoute,
})

function AttendanceCorrectionsRoute() {
  const children = useChildMatches()
  const navigate = useNavigate()
  const search = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <CorrectionsScreen
      tab={search.tab}
      onTabChange={(tab) =>
        void navigate({ to: '/hr/attendance-corrections', search: { tab }, replace: true })
      }
      freelancePanel={() => (
        <WorkEntriesPanel
          sourceEntryId={search.entry_id}
          onSourceEntryHandled={() =>
            void navigate({
              to: '/hr/attendance-corrections',
              search: { ...search, entry_id: undefined },
              replace: true,
            })
          }
        />
      )}
      remoteWorkPanel={({ companyId, canApprove }) => (
        <HybridWorkRequestsPanel companyId={companyId} canApprove={canApprove} />
      )}
    />
  )
}
